import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import BreathingCue from '@/components/BreathingCue';
import { listSessions, deleteSession } from '@/lib/api';
import { getAnonId, getSettings } from '@/lib/storage';
import EXHALE from '@/lib/testIds';

const MODE_LABELS = {
  free: 'Free Unload',
  midnight_thoughts: 'Midnight Thoughts',
  brain_unload: 'Brain Unload',
  focus_recovery: 'Focus Recovery',
  slow_spiral_reset: 'Slow Spiral Reset',
  emotional_release: 'Emotional Release',
  quiet_reset: 'Quiet Reset',
  mental_declutter: 'Mental Declutter',
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

function truncate(text, n = 200) {
  const t = (text || '').replace(/\s+/g, ' ').trim();
  if (t.length <= n) return t;
  return t.slice(0, n).trimEnd() + '…';
}

export default function History() {
  const settings = getSettings();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('timeline'); // 'timeline' | 'calendar'
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    listSessions(getAnonId())
      .then((data) => { if (active) setSessions(data); })
      .catch(() => { if (active) setSessions([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const handleDelete = async (id) => {
    try {
      await deleteSession(id, getAnonId());
      setSessions((prev) => prev.filter((s) => s.id !== id));
      toast('quietly removed', { duration: 1800 });
    } catch (_) {
      toast('couldn\'t remove just now');
    }
  };

  return (
    <section
      data-testid={EXHALE.historyRoot}
      className="relative min-h-[calc(100vh-6rem)] px-6 sm:px-12 py-16 sm:py-20"
    >
      <BreathingCue active={settings.breathing} dimmed />

      <div className="relative z-10 mx-auto max-w-2xl">
        <p className="eyebrow">moments of release</p>
        <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl tracking-tight">
          Quiet Moments
        </h2>

        <div className="mt-10 flex items-center gap-7 text-sm">
          <button
            type="button"
            data-testid={EXHALE.tabTimeline}
            onClick={() => setView('timeline')}
            className={`quiet-link ${view === 'timeline' ? 'opacity-100' : 'opacity-50 hover:opacity-100'}`}
          >
            timeline
          </button>
          <button
            type="button"
            data-testid={EXHALE.tabCalendar}
            onClick={() => setView('calendar')}
            className={`quiet-link ${view === 'calendar' ? 'opacity-100' : 'opacity-50 hover:opacity-100'}`}
          >
            calendar
          </button>
        </div>

        {loading && (
          <p className="mt-12 text-sm text-muted-foreground">loading quietly…</p>
        )}

        {!loading && sessions.length === 0 && (
          <div className="mt-16">
            <p className="eyebrow">welcome</p>
            <h3 className="mt-3 font-serif text-3xl sm:text-4xl font-light tracking-tight leading-tight">
              Your first exhale starts here.
            </h3>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-foreground/70">
              You haven&apos;t saved any reset sessions yet. When you&apos;re ready, start a
              Brain Unload, Quiet Reset, or Inhale&nbsp;/&nbsp;Exhale session — and a small,
              soft mark will appear here.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Link
                to="/session/free"
                data-testid={EXHALE.emptyBeginExhale}
                className="inline-flex items-center gap-3 rounded-full bg-charcoal pl-6 pr-5 py-3 text-sand transition-all duration-500 hover:pl-7 hover:pr-4"
              >
                <span className="text-base">Begin an Exhale</span>
                <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <Link
                to="/session/brain_unload"
                data-testid={EXHALE.emptyBrainUnload}
                className="quiet-link text-base opacity-80 hover:opacity-100"
              >
                Brain Unload
              </Link>
              <Link
                to="/session/quiet_reset"
                className="quiet-link text-base opacity-60 hover:opacity-100"
              >
                Quiet Reset
              </Link>
            </div>
          </div>
        )}

        {!loading && sessions.length > 0 && view === 'timeline' && (
          <ul className="mt-12 flex flex-col">
            {sessions.map((s, i) => (
              <li
                key={s.id}
                data-testid={EXHALE.timelineItem(s.id)}
              >
                {i > 0 && <div className="quiet-dash" />}
                <article className="group py-7">
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="eyebrow">{formatDate(s.created_at)}</p>
                    <button
                      type="button"
                      data-testid={EXHALE.deleteSession(s.id)}
                      onClick={() => handleDelete(s.id)}
                      className="opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity duration-500"
                      aria-label="Remove this session"
                    >
                      <Trash2 size={14} strokeWidth={1.25} />
                    </button>
                  </div>

                  <h3 className="mt-2 font-serif text-xl sm:text-2xl font-light tracking-tight">
                    {MODE_LABELS[s.mode] || s.mode}
                  </h3>

                  <p className="mt-3 text-base leading-relaxed text-foreground/75">
                    {expandedId === s.id ? s.content : truncate(s.content, 240)}
                  </p>

                  {s.reflection && (
                    <p className="mt-4 font-serif italic text-foreground/65">
                      “{s.reflection}”
                    </p>
                  )}

                  <div className="mt-4 flex items-center gap-6 text-xs text-muted-foreground">
                    <span>{s.word_count} words</span>
                    {(s.content || '').length > 240 && (
                      <button
                        type="button"
                        onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                        className="quiet-link opacity-70 hover:opacity-100"
                      >
                        {expandedId === s.id ? 'fold away' : 'open fully'}
                      </button>
                    )}
                  </div>
                </article>
              </li>
            ))}
          </ul>
        )}

        {!loading && sessions.length > 0 && view === 'calendar' && (
          <CalendarView sessions={sessions} />
        )}
      </div>
    </section>
  );
}

function CalendarView({ sessions }) {
  // Build a soft 6-row grid for the current calendar month.
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const byDay = useMemo(() => {
    const map = new Map();
    sessions.forEach((s) => {
      try {
        const d = new Date(s.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        map.set(key, (map.get(key) || 0) + 1);
      } catch (err) {
        // Defensive only — backend always emits ISO strings. Log quietly.
        if (typeof console !== 'undefined') console.debug('skip malformed created_at', err);
      }
    });
    return map;
  }, [sessions]);

  const firstOfMonth = new Date(cursor.year, cursor.month, 1);
  const startWeekday = firstOfMonth.getDay(); // 0..6
  const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = firstOfMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' });

  const move = (delta) => {
    const m = cursor.month + delta;
    const y = cursor.year + Math.floor(m / 12);
    setCursor({ year: y, month: ((m % 12) + 12) % 12 });
  };

  return (
    <div className="mt-12">
      <div className="flex items-center justify-between">
        <p className="font-serif text-xl font-light tracking-tight">{monthLabel}</p>
        <div className="flex items-center gap-5 text-sm">
          <button type="button" onClick={() => move(-1)} className="quiet-link opacity-70 hover:opacity-100">earlier</button>
          <button type="button" onClick={() => move(1)} className="quiet-link opacity-70 hover:opacity-100">later</button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-7 gap-3 text-center">
        {['s','m','t','w','t','f','s'].map((w, idx) => (
          <span key={`${w}-${idx}`} className="eyebrow opacity-60">{w}</span>
        ))}
        {cells.map((d, idx) => {
          if (d === null) return <span key={`pad-${idx}`} className="aspect-square" />;
          const iso = `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const count = byDay.get(iso) || 0;
          const intensity = Math.min(count, 4);
          return (
            <div
              key={iso}
              data-testid={EXHALE.calendarDay(iso)}
              className="aspect-square flex flex-col items-center justify-center"
            >
              <span className="text-xs text-foreground/60">{d}</span>
              <span
                aria-hidden="true"
                className="mt-1 block rounded-full transition-opacity"
                style={{
                  width: 6 + intensity * 2,
                  height: 6 + intensity * 2,
                  opacity: count === 0 ? 0.12 : 0.45 + intensity * 0.12,
                  background: 'var(--exhale-sage)',
                }}
              />
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-xs text-muted-foreground">
        a small soft mark for each day you returned to exhale.
      </p>
    </div>
  );
}
